'use client';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Conditional from '@next-app/components/Conditional';
import {
  AppliedPromotion,
  CheckoutServerErrorResponse,
  ShippingAddressResponse,
  CommerceItemInfo,
} from '@next-app/lib/features/checkout/Checkout.interface';
import {
  getFormattedPrice,
  isNotEmptyObject,
  isNotNullOrUndefinedorEmpty,
} from '@next-app/utils/common-utils';
import ApplyPromoCodeForm from './ApplyPromoCodeForm';
import LLButton from '@next-app/components/elements/LLButton/LLButton';
import {
  selectDynSess,
  selectIsFurniture,
  selectLlmAPIVersion,
} from '@next-app/lib/features/InitSelectors';
import { removePromoCode } from '@next-app/utils/CheckoutHelper';
import { setLoadCheckoutDetails } from '@next-app/lib/features/checkout/CheckoutSlice';
import LLAlert from '@next-app/components/elements/LLAlert/LLAlert';
import { MiniCartItem } from '@next-app/constants/CheckoutConstants';
import { ShoppingCartData } from '@next-app/lib/features/ShoppingCart/ShoppingCart.interface';
import { setShoppingCartData } from '@next-app/lib/features/ShoppingCart/ShoppingCartSlice';
import { DynamicDTMEvent } from '@next-app/utils/dtm-event';
import { selectIsGSAUser } from '@next-app/lib/features/InitSelectors';
import Loader from '@next-app/components/shared/Loader/Loader';

const PromoCode = ({
  pageType,
  cartSummary,
  commerceItems,
}: {
  pageType: 'checkout' | 'shopping-cart';
  cartSummary: any;
  commerceItems: any;
}) => {
  /**
   * UseState
   */

  const [showLoyaltyMessage, setShowLoyaltyMessage] = useState(false);
  const [hasPromoApplied, setHasPromoApplied] = useState(false);
  const [appliedPromotions, setAppliedPromotions] = useState<
    AppliedPromotion[]
  >([]);
  const [qualifierMessages, setQualifierMessages] = useState<string[]>([]);
  const [appliedCouponCode, setAppliedCouponCode] = useState<string>('');
  const [showCouponCodeInput, setShowCouponCodeInput] = useState<boolean>(true);
  const [fieldError, setFieldError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  /**
   *  dispatch
   */
  const dispatch = useDispatch();

  /**
   *  Selectors
   */
  const dynSessConf = useSelector(selectDynSess);
  const llmAPIVersion = useSelector(selectLlmAPIVersion);
  const isFurniture = useSelector(selectIsFurniture);
  const isGSAUser = useSelector(selectIsGSAUser);

  /**
   * UseEffect
   */
  useEffect(() => {
    if (cartSummary) {
      setHasPromoApplied(cartSummary?.hasPromoApplied);
      setAppliedPromotions(cartSummary?.appliedPromotions);
      setQualifierMessages(cartSummary?.qualifierMessages);
      setShowCouponCodeInput(cartSummary?.isShowCouponbox);
      setAppliedCouponCode(cartSummary?.couponCode);
    }
  }, [cartSummary]);

  useEffect(() => {
    const showVipMessage =
      hasPromoApplied &&
      appliedPromotions.length > 0 &&
      hasAllFurnitureItems(commerceItems);
    setShowLoyaltyMessage(showVipMessage);
  }, [hasPromoApplied, appliedPromotions, commerceItems]);

  /**
   * Method
   */
  const hasAllFurnitureItems = (items: CommerceItemInfo[]) => {
    if (!items || items.length === 0) return false;
    const hasItemsFromFurniture = items.some(
      (item) =>
        !item.furniture &&
        (item.lineGroupHeader === MiniCartItem.ship ||
          item.lineGroupHeader === MiniCartItem.none),
    );
    return hasItemsFromFurniture;
  };

  /**
   * Remove promo code and revert to input box
   */
  const removePromoCodeHandler = async (couponCode: string) => {
    try {
      if (couponCode) {
        setLoading(true);
        const response = await removePromoCode(
          {
            pageName: pageType,
            revokeCouponCode: couponCode,
            _dynSessConf: dynSessConf,
          },
          isFurniture,
          dispatch,
        );

        const removePromoCodeResponse = response as (
          | ShippingAddressResponse
          | ShoppingCartData
        ) &
          CheckoutServerErrorResponse;
        if (isNotEmptyObject(removePromoCodeResponse)) {
          if (
            pageType === 'checkout' &&
            'checkoutResponse' in removePromoCodeResponse
          ) {
            const eventData = {
              event: 'removeCoupon',
              detail: {
                couponCode: couponCode,
                fromPage: 'checkoutPage',
                isFurniture: isFurniture,
                llmAPIVersion: llmAPIVersion,
              },
            };
            DynamicDTMEvent(eventData);
            dispatch(
              setLoadCheckoutDetails(removePromoCodeResponse?.checkoutResponse),
            );
          } else if (
            pageType === 'shopping-cart' &&
            'shoppingCartResponse' in removePromoCodeResponse
          ) {
            const eventData = {
              event: 'removeCoupon',
              detail: {
                couponCode: couponCode,
                fromPage: 'shopping-cart',
                isFurniture: isFurniture,
                llmAPIVersion: llmAPIVersion,
              },
            };
            DynamicDTMEvent(eventData);
            dispatch(
              setShoppingCartData(
                removePromoCodeResponse?.shoppingCartResponse,
              ),
            );
          }
        }
      }
    } catch (error) {
      console.error('Error removing promo code:', error);
      // Optionally show an error message to the user here
    } finally {
      setLoading(false); // Stop loader
    }
  };

  /**
   *
   * @returns Display Flat Rate Shipping Messaging for VIP and VIP Gold Members
   */
  const ShippingMessage = (
    { message, discountAmount }: AppliedPromotion,
    index: number,
  ) => {
    const renderedMessage =
      discountAmount !== 0
        ? message?.replace(
            /\$(\S+)/,
            () =>
              `<strong class="fw-normal" style="color: #00ADEF;">${getFormattedPrice(discountAmount)}</strong>`,
          )
        : message;

    return (
      <p
        className="mt-ll-3 fw-light"
        dangerouslySetInnerHTML={{ __html: renderedMessage }}
        key={`promoMessage${index}`}
      ></p>
    );
  };

  /**
   *
   * Promo Code applied function
   */
  const RenderPromoApplied = (item: AppliedPromotion, index: number) => {
    const couponCode = item?.couponCode || appliedCouponCode;
    return (
      <div
        className="d-flex justify-content-between mt-ll-3"
        key={`promoMessage${index}`}
      >
        <span>
          Promo Code{' '}
          <strong className="fw-normal tc-18b335 text-uppercase">
            {couponCode}
          </strong>{' '}
          has been applied.
        </span>
        <LLButton
          type="button"
          customClass="ls-icon icn-trash-o border-0 p-0 bg-transparent"
          onClick={() => removePromoCodeHandler(couponCode)}
        ></LLButton>
      </div>
    );
  };

  /**
   *
   * @returns
   * Qualifier Messages rendered method
   */
  const RenderQualifierMessages = () => {
    return (
      <>
        <LLAlert
          type={'warning'}
          customClass={'mt-ll-3 d-block flex-column p-ll-3 pb-2'}
        >
          {qualifierMessages?.map((item, index: number) => (
            <p
              className="pb-2 fw-light"
              key={`qualifierMessages${index}`}
              dangerouslySetInnerHTML={{ __html: item }}
            ></p>
          ))}
        </LLAlert>
      </>
    );
  };

  return (
    <>
      <div className="my-lg-ll-5 mb-ll-5 d-print-none">
        <Conditional if={!isGSAUser}>
          <>
            <Conditional if={showCouponCodeInput}>
              <ApplyPromoCodeForm
                setFieldError={setFieldError}
                fieldError={fieldError}
                pageType={pageType}
                appliedCouponCode={appliedCouponCode}
              />
            </Conditional>
          </>
        </Conditional>
        <Conditional if={isNotNullOrUndefinedorEmpty(appliedPromotions)}>
          <>
            {appliedPromotions?.map((item: AppliedPromotion, index) => {
              if (item.isGlobalPromo) {
                return ShippingMessage(item, index);
              }
              if (!item.isGlobalPromo) {
                return RenderPromoApplied(item, index);
              }
            })}
          </>
        </Conditional>
        <Conditional if={qualifierMessages?.length > 0 && !fieldError}>
          <RenderQualifierMessages />
        </Conditional>
      </div>
      <Conditional if={loading}>
        <Loader aria-label="Promo code apply" />
      </Conditional>
    </>
  );
};

export default PromoCode;
